/**
 * TG-style модалка предпросмотра при отправке нескольких файлов.
 * Открывается при выборе/drop/paste файлов. Внутри:
 * - Большие превью (вертикальный скролл)
 * - Лента thumbnails внизу (кликабельная — скролл к выбранному)
 * - Caption-textarea (текст сообщения)
 * - Кнопки: Add (добавить ещё), Cancel (отменить всё), Send (отправить)
 *
 * Файлы аплоадятся в фоне (parent держит uploadingFiles/pendingFiles).
 * При Send — вызываем onSend(), кнопка disabled пока есть uploadingFiles.
 */
import { useEffect, useMemo, useRef } from "react";
import { createPortal } from "react-dom";

import { Icon } from "@/components/prototype/Icon";

import type { ChatAttachment } from "@/api/dashboard";
import { chatAttachmentPreviewUrl, chatVideoThumbnailUrl } from "@/api/dashboard";


type UploadingItem = {
  tempId: string;
  file: File;
  thumbUrl: string | null;
  isImage: boolean;
  isVideo: boolean;
  loaded: number;
  total: number;
};

type PreviewItem =
  | { kind: "uploading"; tempId: string; isImage: boolean; isVideo: boolean; thumbUrl: string | null; filename: string; pct: number }
  | { kind: "ready"; id: number; isImage: boolean; isVideo: boolean; thumbUrl: string; filename: string };


function isImageMime(mime: string | null | undefined, filename?: string): boolean {
  const m = (mime || "").toLowerCase();
  if (m.startsWith("image/")) return true;
  const f = (filename || "").toLowerCase();
  return /\.(png|jpe?g|gif|webp|bmp|svg|heic|heif)$/i.test(f);
}
function isVideoMime(mime: string | null | undefined, filename?: string): boolean {
  const m = (mime || "").toLowerCase();
  if (m.startsWith("video/")) return true;
  if (m.startsWith("audio/")) return false;
  const f = (filename || "").toLowerCase();
  return /\.(mp4|mov|m4v|webm|avi|mkv)$/i.test(f);
}


export function ImageUploadModal({
  uploadingFiles, pendingFiles, text, onTextChange, onCancel, onSend, onAddMore, onRemoveUploading, onRemovePending, isSending,
}: {
  uploadingFiles: UploadingItem[];
  pendingFiles: ChatAttachment[];
  text: string;
  onTextChange: (s: string) => void;
  onCancel: () => void;
  onSend: () => void;
  onAddMore: () => void;
  onRemoveUploading: (tempId: string) => void;
  onRemovePending: (id: number) => void;
  isSending: boolean;
}) {
  const stageRef = useRef<HTMLDivElement | null>(null);
  const captionRef = useRef<HTMLTextAreaElement | null>(null);

  const items: PreviewItem[] = useMemo(() => {
    const a: PreviewItem[] = uploadingFiles.map(u => ({
      kind: "uploading" as const,
      tempId: u.tempId,
      isImage: u.isImage, isVideo: u.isVideo,
      thumbUrl: u.thumbUrl,
      filename: u.file.name,
      pct: u.total > 0 ? Math.min(100, (u.loaded / u.total) * 100) : 0,
    }));
    const b: PreviewItem[] = pendingFiles.map(f => ({
      kind: "ready" as const,
      id: f.id,
      isImage: isImageMime(f.mime, f.filename),
      isVideo: isVideoMime(f.mime, f.filename),
      thumbUrl: isVideoMime(f.mime, f.filename)
        ? chatVideoThumbnailUrl(f.id)
        : chatAttachmentPreviewUrl(f.id),
      filename: f.filename,
    }));
    return [...a, ...b];
  }, [uploadingFiles, pendingFiles]);

  const total = items.length;
  const stillUploading = uploadingFiles.length > 0;

  // Initial focus → caption + scroll-lock body
  useEffect(() => {
    captionRef.current?.focus();
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, []);

  // Keyboard
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onCancel();
      } else if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
        e.preventDefault();
        if (!stillUploading && total > 0) onSend();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onCancel, onSend, stillUploading, total]);

  function getKey(it: PreviewItem): string {
    return it.kind === "uploading" ? `u:${it.tempId}` : `r:${it.id}`;
  }
  function removeItem(it: PreviewItem): void {
    if (it.kind === "uploading") onRemoveUploading(it.tempId);
    else onRemovePending(it.id);
  }

  return createPortal(
    <div className="img-upload-modal" role="dialog" aria-modal="true" onClick={onCancel}>
      <div className="img-upload-card" onClick={(e) => e.stopPropagation()}>
        <header className="img-upload-header">
          <h3 className="img-upload-title">
            {total} {total === 1 ? "файл выбран" : total < 5 ? "файла выбрано" : "файлов выбрано"}
          </h3>
        </header>

        <div className="img-upload-stage" ref={stageRef} data-count={items.length}>
          {items.length === 0 && (
            <div className="img-upload-empty">Нет файлов</div>
          )}
          {items.map(it => {
            const key = getKey(it);
            return (
              <div
                key={key}
                className="img-upload-stage-cell"
              >
                {it.isImage && it.thumbUrl && (
                  <img src={it.thumbUrl} alt={it.filename} draggable={false}/>
                )}
                {it.isVideo && it.thumbUrl && (
                  <>
                    <img src={it.thumbUrl} alt={it.filename} draggable={false}/>
                    <span className="img-upload-stage-play"><Icon name="play" size={28}/></span>
                  </>
                )}
                {!it.isImage && !it.isVideo && (
                  <div className="img-upload-stage-file">
                    <Icon name="file" size={42}/>
                    <span>{it.filename}</span>
                  </div>
                )}
                {it.kind === "uploading" && (
                  <div className="img-upload-stage-progress">
                    <div className="img-upload-stage-progress-bar" style={{ width: `${it.pct}%` }}/>
                  </div>
                )}
                <button
                  type="button"
                  className="img-upload-stage-remove"
                  onClick={() => removeItem(it)}
                  title="Удалить"
                >
                  <Icon name="x" size={14}/>
                </button>
              </div>
            );
          })}
        </div>

        <div className="img-upload-caption-row">
          <textarea
            ref={captionRef}
            className="img-upload-caption"
            placeholder="Подпись…"
            value={text}
            onChange={(e) => onTextChange(e.target.value)}
            rows={2}
          />
        </div>

        <footer className="img-upload-footer">
          <button type="button" className="img-upload-btn-link" onClick={onAddMore}>
            Добавить
          </button>
          <div className="img-upload-footer-spacer"/>
          <button type="button" className="img-upload-btn-link" onClick={onCancel}>
            Отмена
          </button>
          <button
            type="button"
            className="img-upload-btn-primary"
            onClick={onSend}
            disabled={stillUploading || total === 0 || isSending}
            title={stillUploading ? "Подождите завершения загрузки" : "Отправить (Ctrl+Enter)"}
          >
            {stillUploading ? "Загрузка…" : "Отправить"}
          </button>
        </footer>
      </div>
    </div>,
    document.body,
  );
}
